import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { SQRT_META, type SqrtParams } from './sqrt.model';

/** Sqrt params with index signature for JsxParamStore compatibility */
type SqrtParamsIndexed = SqrtParams & { [key: string]: number };

/**
 * Creates square root functions: a√(x-h) + k
 *
 * Parameters:
 * - a: vertical scale/reflection
 * - h: horizontal shift (start of domain)
 * - k: vertical shift
 */
export class SqrtCreator extends BaseFunctionCreator<SqrtParamsIndexed> {
  readonly functionType = 'sqrt';
  readonly defaultParams: SqrtParamsIndexed = { ...SQRT_META.defaultParams };
  readonly availableFeatures = SQRT_META.availableFeatures;
  readonly handleConfigs = SQRT_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as SqrtParamsIndexed;

    const colorName = (display?.curve?.color ?? 'amber') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: SQRT_META.defaultRanges.a },
      h: { value: initialParams.h, range: SQRT_META.defaultRanges.h },
      k: { value: initialParams.k, range: SQRT_META.defaultRanges.k },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      const arg = x - h;
      if (arg < 0) {
        return NaN;
      }
      return a * Math.sqrt(arg) + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // Derivative of a√(x-h) + k is a / (2√(x-h))
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          const arg = x - h;
          if (arg <= 0) {
            return NaN;
          }
          return a / (2 * Math.sqrt(arg));
        };
      }
      if (order === 2) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          const arg = x - h;
          if (arg <= 0) {
            return NaN;
          }
          return -a / (4 * Math.pow(arg, 1.5));
        };
      }
      // General formula: a * (-1)^(n-1) * (2n-3)!! / (2^n * (x-h)^((2n-1)/2))
      return (x: number) => {
        const a = paramStore.getValue('a');
        const h = paramStore.getValue('h');
        const arg = x - h;
        if (arg <= 0) {
          return NaN;
        }
        const sign = order % 2 === 1 ? 1 : -1;
        // Double factorial: (2n-3)!! = 1 * 3 * 5 * ... * (2n-3)
        let doubleFactorial = 1;
        for (let i = 1; i <= 2 * order - 3; i += 2) {
          doubleFactorial *= i;
        }
        return (sign * a * doubleFactorial) / (Math.pow(2, order) * Math.pow(arg, (2 * order - 1) / 2));
      };
    };

    return {
      id,
      type: 'sqrt',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as SqrtParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
